import { useCallback, useEffect, useState } from "react";
import { getEvent, updateEvent } from "src/api/events";
import { useEventForm } from "src/pages/CreateEventPage/hooks/useEventForm";
import { EventFormData } from "src/pages/CreateEventPage/types";
import { EventType } from "src/types/events";
import { getEditableEventTitle } from "src/pages/CreateEventPage/utils/eventTitle";

interface UseUpdateEventFormOptions {
  eventId?: string;
  onUpdated: () => void;
}

const toDateTimeLocal = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const timezoneOffsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
};

const normalizeType = (type: number): EventType => {
  if (type === EventType.Game || type === EventType.Meeting) {
    return type;
  }
  return EventType.Practice;
};

const mapEventToFormData = (event: Awaited<ReturnType<typeof getEvent>>): EventFormData => ({
  title: getEditableEventTitle(event),
  description: event.description ?? "",
  startTime: toDateTimeLocal(event.startTime),
  durationMinutes: event.durationMinutes ?? 75,
  locationName: event.locationName ?? "",
  locationAddress: event.locationAddress ?? "",
  iceRinkNumber: event.iceRinkNumber ?? "",
  leagueName: event.leagueName ?? "",
  homeTeamName: event.homeTeamName ?? "",
  awayTeamName: event.awayTeamName ?? "",
  uniformColorId: event.uniformColorId ?? "",
  selectedExerciseIds: event.exercises?.map((exercise) => exercise.id) ?? [],
  useAddressSearch: true,
  type: normalizeType(event.type),
});

export const useUpdateEventForm = ({ eventId, onUpdated }: UseUpdateEventFormOptions) => {
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [isExternalEvent, setIsExternalEvent] = useState(false);

  const {
    formData,
    updateField,
    handleTypeChange,
    handleSubmit: submitForm,
    loading,
    error,
    isGame,
    isMeeting,
    isPractice,
    setFormData,
    setError,
  } = useEventForm({
    teamId,
    submitEvent: async (dto, currentUserId) => {
      if (!eventId) {
        throw new Error("ID события не указан");
      }
      await updateEvent(eventId, dto, currentUserId);
    },
  });

  useEffect(() => {
    if (!eventId) {
      setError("ID события не указан");
      setLoadingInitial(false);
      return;
    }

    let active = true;
    setLoadingInitial(true);
    setError(null);

    void getEvent(eventId)
      .then((event) => {
        if (!active) {
          return;
        }
        setTeamId(event.teamId ?? null);
        setIsExternalEvent(event.externalLeagueProvider != null);
        setFormData(mapEventToFormData(event));
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        const message = err instanceof Error ? err.message : "Не удалось загрузить событие";
        setError(message);
      })
      .finally(() => {
        if (active) {
          setLoadingInitial(false);
        }
      });

    return () => {
      active = false;
    };
  }, [eventId, setError, setFormData]);

  const handleSubmit = useCallback(async () => {
    const success = await submitForm();
    if (success) {
      onUpdated();
    }
  }, [onUpdated, submitForm]);

  return {
    formData,
    updateField,
    handleTypeChange,
    loadingInitial,
    loading: loadingInitial || loading,
    error,
    isGame,
    isMeeting,
    isPractice,
    teamId,
    isExternalEvent,
    handleSubmit,
  };
};
